import React from 'react';
import moment from 'moment';
import { Transition } from 'react-spring/renderprops';
import { AuthContext, AgentsContext, ListingLabelContext } from 'contexts';
import { ListingLabel } from '@bb/automaton';
import { UserRoles } from '@bb/users';
import { useBreakpoint } from 'hooks';
import {
  DatePicker,
  Icon,
  Text,
  Select,
  SelectOption,
  Input,
  InputNumber,
  Divider,
  ListItem,
  Contact,
  Required,
  UserSelect,
  Card,
} from 'components';
import {
  Country,
  CountrySubdivisions,
  StateProvinces,
  StateProvinceShortName,
} from '@bb/domain/dist/types';

import { DefaultOptionType } from 'antd/lib/select';
import { CreateListingCardProps, ListingDetailsFields } from '../types';
import {
  StyledAgentList,
  StyledGrid,
  StyledAlert,
  StyledSubdivisionName,
} from './StyledComponents';
import { countries } from '../../../../app/constants';

type ListingDetailsProps = CreateListingCardProps<ListingDetailsFields> & {
  errorMessage?: string;
  formFieldsMissing?: boolean;
  editing?: boolean;
  hasNarSettlement?: boolean;
};

const ListingDetails: React.FC<ListingDetailsProps> = ({
  editing,
  errorMessage,
  formFieldsMissing,
  type,
  subtype,
  line1,
  line2,
  country,
  province,
  city,
  neighborhood,
  postal_code,
  list_price,
  list_price_type,
  expiry,
  commission,
  status,
  coming_soon_date,
  reps,
  setListing,
  hasNarSettlement,
}) => {
  const { md } = useBreakpoint();
  const { auth } = React.useContext(AuthContext);
  const { orgUsersMap } = React.useContext(AgentsContext);
  const { getLabel, getDefaultLabel, isHidden } =
    React.useContext(ListingLabelContext);

  const [provinces, setProvinces] = React.useState(
    country
      ? Object.keys(CountrySubdivisions[country])
      : Object.keys(StateProvinces).sort(),
  );
  const [sortByShort, setSortByShort] = React.useState(true);

  const disabled = !auth.isAdmin && editing;
  const isAgent = auth.role === UserRoles.Agent;
  const isLicensedAgent =
    isAgent || (auth.role === UserRoles['Team Admin'] && auth.isLicensed);
  const isCreatingAsAgent = !editing && isLicensedAgent;

  const addRep = (values: string[]) => {
    setListing?.(prev => ({
      ...prev,
      reps: [...prev.reps, ...values.map(id => orgUsersMap![id])],
    }));
  };

  const removeRep = (id: string) => {
    setListing?.(prev => ({
      ...prev,
      reps: prev.reps.filter(rep => rep.id !== id),
    }));
  };

  const handleCountryChange = (value: Country) => {
    // clear if selected province/state is not in the new provinces/states
    const shouldClearProvince = !Object.prototype.hasOwnProperty.call(
      CountrySubdivisions[value],
      province,
    );
    setProvinces(Object.keys(CountrySubdivisions[value]));
    setListing?.(prev => ({
      ...prev,
      country: value,
      ...(shouldClearProvince && { province: '' }),
    }));
  };

  const handleSearchChange = (searchValue: string) => {
    setSortByShort(false);
    if (searchValue.length <= 2) {
      const searchRegExp = new RegExp(searchValue, 'i');
      setSortByShort(provinces.some(option => option.match(searchRegExp)));
    }
  };

  React.useEffect(() => {
    if (isCreatingAsAgent && !reps.length) {
      setListing?.(prev => ({
        ...prev,
        reps: [{ ...auth, team: auth.team?.toString() }],
      }));
    }
  }, [auth]);

  return (
    <Card
      type="primary"
      title="Listing Details"
      id="listingDetails"
      subtitle={
        <>
          {' '}
          <Required />
          <Text type="secondary"> = Mandatory</Text>
        </>
      }
    >
      {errorMessage && <StyledAlert showIcon message={errorMessage} />}
      <StyledGrid $columns={3}>
        <Select
          label="Listing Type"
          required
          disabled={editing}
          error={formFieldsMissing && !type}
          value={type}
          onChange={value => {
            setListing?.(prev => ({
                ...prev,
                type: value,
                subtype: value !== 'commercial' ? prev.subtype : 'sale',
              }));
          }}
          options={[
            { value: 'condo', label: getLabel('CONDO') },
            { value: 'freehold', label: getLabel('FREEHOLD') },
            { value: 'land', label: getLabel('LAND') },
            { value: 'income', label: getLabel('INCOME') },
            { value: 'commercial', label: getLabel('COMMERCIAL') },
          ]}
        />
        {type !== 'commercial' && (
          <Select
            label="Transaction Type"
            required
            disabled={editing}
            error={formFieldsMissing && !subtype}
            value={subtype}
            onChange={value =>
              setListing?.(prev => ({ ...prev, subtype: value }))
            }
            options={[
              { value: 'sale', label: 'Sale' },
              { value: 'lease', label: 'Lease' },
            ]}
          />
        )}
      </StyledGrid>
      {type && subtype && (
        <>
          <Divider />
          <StyledGrid $columns={3}>
            <Input
              label="Address"
              required
              value={line1}
              error={formFieldsMissing && !line1}
              onChange={({ target: { value } }) =>
                setListing?.(prev => ({ ...prev, line1: value }))
              }
              disabled={disabled}
              data-cy="listing-line1-address"
            />
            {type === 'condo' && (
              <Input
                label="Unit Number"
                value={line2}
                onChange={({ target: { value } }) =>
                  setListing?.(prev => ({ ...prev, line2: value }))
                }
                data-cy="listing-line2-unit"
              />
            )}
            <Input
              label={getLabel('NEIGHBORHOOD')}
              required
              error={formFieldsMissing && !neighborhood}
              value={neighborhood}
              onChange={({ target: { value } }) =>
                setListing?.(prev => ({ ...prev, neighborhood: value }))
              }
              data-cy="listing-neighborhood"
            />
            <Input
              label="City"
              required
              error={formFieldsMissing && !city}
              value={city}
              onChange={({ target: { value } }) =>
                setListing?.(prev => ({ ...prev, city: value }))
              }
              data-cy="listing-city"
            />
            <Select
              label="State/Province"
              required
              showSearch
              optionFilterProp={sortByShort ? 'value' : 'longForm'}
              error={formFieldsMissing && !province}
              value={province}
              onChange={value => {
                setListing?.(prev => ({ ...prev, province: value }));
              }}
              onSearch={searchValue => handleSearchChange(searchValue)}
              data-cy="listing-province"
            >
              {provinces.map((provinceOrState: StateProvinceShortName) => (
                <SelectOption
                  key={provinceOrState}
                  value={provinceOrState}
                  longForm={StateProvinces[provinceOrState]}
                >
                  <StyledSubdivisionName>
                    <Text>{provinceOrState}</Text>
                    <Text type="secondary">
                      {StateProvinces[provinceOrState]}
                    </Text>
                  </StyledSubdivisionName>
                </SelectOption>
              ))}
            </Select>

            <Input
              label="Zip/Postal Code"
              required
              error={formFieldsMissing && !postal_code}
              value={postal_code}
              onChange={({ target: { value } }) =>
                setListing?.(prev => ({ ...prev, postal_code: value }))
              }
              data-cy="listing-postal-code"
            />

            <Select
              label="Country"
              required
              error={formFieldsMissing && !country}
              value={country}
              onChange={value => handleCountryChange(value)}
              options={countries}
              data-cy="listing-country"
            />
          </StyledGrid>
          <Divider />
          <StyledGrid $columns={3}>
            <InputNumber
              label="List Price"
              type="currency"
              required
              error={formFieldsMissing && !list_price}
              disabled={editing}
              value={list_price}
              onChange={(value: number) =>
                setListing?.(prev => ({ ...prev, list_price: value }))
              }
              data-cy="listing-list-price"
            />
            {type === 'commercial' && (
              <Select
                allowClear
                label="Transaction Type"
                required
                error={formFieldsMissing && !list_price_type}
                value={list_price_type}
                onChange={value =>
                  setListing?.(prev => ({ ...prev, list_price_type: value }))
                }
                options={[
                  { label: 'For Sale', value: 'For Sale' },
                  { label: 'Gross Lease', value: 'Gross Lease' },
                  { label: 'Life Lease', value: 'Life Lease' },
                  { label: 'Month', value: 'Month' },
                  { label: 'Net Lease', value: 'Net Lease' },
                  { label: 'Other', value: 'Other' },
                  { label: 'Per Acre', value: 'Per Acre' },
                  { label: 'Per Lot', value: 'Per Lot' },
                  { label: 'Per Sq Ft', value: 'Per Sq Ft' },
                  { label: 'Per Unit', value: 'Per Unit' },
                  { label: 'Per Week', value: 'Per Week' },
                  { label: 'Per Year', value: 'Per Year' },
                  { label: 'Plus Lot', value: 'Plus Lot' },
                  { label: 'Plus Stock', value: 'Plus Stock' },
                  { label: 'Sq Ft Gross', value: 'Sq Ft Gross' },
                  { label: 'Sq Ft Net', value: 'Sq Ft Net' },
                  { label: 'Sq M Gross', value: 'Sq M Gross' },
                  { label: 'Sq M Net', value: 'Sq M Net' },
                ]}
                data-cy="listing-list-price-type"
              />
            )}
            <DatePicker
              label="Expiry Date"
              required
              error={formFieldsMissing && !expiry}
              value={expiry}
              onChange={value =>
                setListing?.(prev => ({ ...prev, expiry: value }))
              }
              disabledDate={date => {
                const today = moment().startOf('day');
                return today.isSameOrAfter(date);
              }}
              disabled={disabled}
              format="MMM D, YYYY"
              data-cy="listing-expiry-date"
            />
            <Select
              allowClear
              label={getLabel('Status')}
              required
              error={formFieldsMissing && !status}
              value={status}
              onChange={value =>
                setListing?.(prev => ({
                  ...prev,
                  status: value,
                  coming_soon_date: undefined,
                }))
              }
              options={(
                [
                  'ASSIGNMENT',
                  'COMING_SOON',
                  'EXCLUSIVE',
                  'NEW_CONSTRUCTION',
                  'MLS',
                ] as ListingLabel[]
              ).reduce((acc, label) => {
                // additional logic specific to this page
                if (label === 'MLS' && !auth.isAdmin) return acc;

                if (!isHidden(label))
                  acc.push({
                    value: getDefaultLabel(label),
                    label: getLabel(label),
                  });
                return acc;
              }, [] as DefaultOptionType[])}
              disabled={disabled}
              data-cy="listing-status"
            />
            {!hasNarSettlement && (
              <Input
                label="Buyer Agent's Commission"
                value={commission}
                onChange={({ target: { value } }) =>
                  setListing?.(prev => ({ ...prev, commission: value }))
                }
                data-cy="listing-agent-commission"
              />
            )}
            {status === 'Coming Soon' && (
              <DatePicker
                label="Coming Soon Date"
                required
                error={formFieldsMissing && !coming_soon_date}
                value={coming_soon_date}
                onChange={value =>
                  setListing?.(prev => ({ ...prev, coming_soon_date: value }))
                }
                disabledDate={date => {
                  const today = moment().startOf('day');
                  return today.isSameOrAfter(date);
                }}
                format="MMM D, YYYY"
                data-cy="coming-soon-date"
              />
            )}
          </StyledGrid>
          <Divider />
          <StyledGrid $columns={3} $alignItems="flex-start">
            <div>
              <Text strong>
                Listing Agent <Required />
              </Text>
              <StyledAgentList>
                <Transition
                  items={reps}
                  keys={rep => rep.id}
                  from={{ opacity: 0, height: 0 }}
                  enter={{ opacity: 1, height: 53 }}
                  leave={{ opacity: 0, height: 0 }}
                >
                  {rep => props => (
                    <ListItem style={props}>
                      <Contact contact={rep} showBranchName avatarSize={32} />
                      {!(isAgent && rep.id === auth.id) && (
                        <button onClick={() => removeRep(rep.id)}>
                          <Icon type="x" />
                        </button>
                      )}
                    </ListItem>
                  )}
                </Transition>
              </StyledAgentList>
            </div>
            <UserSelect
              showSearch
              error={formFieldsMissing && reps.length === 0}
              value={[]}
              label="Listing Agent"
              listHeight={md ? 250 : 50}
              onChange={addRep}
              filter={({ id }) => !reps.some(rep => rep.id === id)}
              backEndFilter={'listingAgents'}
              data-cy="user-select-label"
            />
          </StyledGrid>
        </>
      )}
    </Card>
  );
};

export default React.memo(ListingDetails);
